import { TypedDocumentNode, gql, useLazyQuery } from '@apollo/client';
import {
  faAngleDown,
  faAngleUp,
  faPlus,
} from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { CSSProperties, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { Link } from '@sorare/routing';

import {
  So5LeaderboardSeasonality,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { SeasonalityIcon } from '@sorare/core/src/atoms/icons/SeasonalityIcon';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM, LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_COMPETITION_COMPETITION_TRCKGRP_TRCK } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useDeeplinkFromNativeApp } from '@sorare/core/src/hooks/useDeeplinkFromNativeApp';
import {
  SESSION_STORAGE,
  useSessionStorage,
} from '@sorare/core/src/hooks/useSessionStorage';
import { useToggle } from '@sorare/core/src/hooks/useToggle';
import { range } from '@sorare/core/src/lib/arrays';
import {
  ensureHexColor,
  getRgbColorFromHex,
} from '@sorare/core/src/lib/colors';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import { PlayerGameStatsPrediction } from '@sorare/marketplace/src/components/game/PlayerGameStatsPrediction';

import emptyAppearance from 'assets/empty_appearance.svg';
import { EligibleCards } from 'components/league/EligibleCards';
import { LeagueDot } from 'components/league/LeagueDot';
import { Badge } from 'components/managerTeam/Badge';
import { getAddTeamPath } from 'lib/so5';

import {
  UpcomingFixtureLeagueQuery,
  UpcomingFixtureLeagueQueryVariables,
  UpcomingFixtureLeague_so5LeaderboardGroupInterface,
} from './__generated__/index.graphql';

const LAZY_UPCOMING_FIXTURE_LEAGUE_QUERY = gql`
  query UpcomingFixtureLeagueQuery($slug: String!) {
    so5 {
      so5LeaderboardGroupInterface(slug: $slug) {
        slug
        mySo5LeaderboardContenders {
          slug
          managerTeam {
            id
            ...ManagerTeamBadge_managerTeam
          }
          so5Leaderboard {
            slug
            rules {
              id
              appearances {
                name
              }
            }
            so5LeagueTrack {
              slug
              groupSlug
            }
            ...ManagerTeamBadge_so5Leaderboard
          }
          so5Lineup {
            id
            confirmable
            so5Appearances {
              id
              index
              avatarUrl: pictureUrl(derivative: "avatar")
              eligiblePlayerGameScores {
                id
                anyPlayerGameStats {
                  id
                  ... on PlayerGameStats {
                    id
                    footballPlayingStatusOdds {
                      ...PlayerGameStatsPrediction_playingStatusOdds
                    }
                  }
                }
              }
            }
          }
        }
        ...getAddTeamPath_so5LeaderboardGroupInterface
      }
    }
  }
  ${PlayerGameStatsPrediction.fragments.playingStatusOdds}
  ${Badge.fragments.managerTeam}
  ${Badge.fragments.so5Leaderboard}
  ${getAddTeamPath.fragments.so5LeaderboardGroupInterface}
` as TypedDocumentNode<
  UpcomingFixtureLeagueQuery,
  UpcomingFixtureLeagueQueryVariables
>;

const Root = styled.div`
  &:not(.hideBorder) {
    border-bottom: 1px solid var(--c-nd-150);
  }
`;
const StyledButtonBase = styled(ButtonBase)`
  width: 100%;
  padding: var(--double-unit) 0;
`;
const Content = styled(Vertical)`
  padding-bottom: var(--double-unit);
`;
const contenderStyle = css`
  min-height: calc(7 * var(--unit));
  padding: var(--intermediate-unit) var(--double-unit);
  border-radius: var(--unit);

  transition: opacity 0.2s ease-in-out;
  &:hover {
    opacity: 0.5;
  }
`;
const Contender = styled.div.attrs({ as: ButtonBase })`
  ${contenderStyle}
  --nb-slots: 5;

  &.baseball {
    --nb-slots: 7;
  }

  background: var(--c-nd-50);

  display: grid;
  grid-template-columns: repeat(
    calc(var(--nb-slots) + 1),
    calc(5 * var(--unit))
  );
  justify-content: space-between;

  > *:first-child {
    align-self: center;
  }
`;
const AvatarSkeleton = styled(Skeleton)`
  width: calc(5 * var(--unit));
  height: calc(5 * var(--unit));
  border-radius: 50%;
`;
const Avatar = styled.img`
  border-radius: 50%;
  object-fit: cover;
`;
const CellWrapper = styled.div`
  width: calc(5 * var(--unit));
  display: flex;
  justify-content: center;
`;
const AddTeam = styled.div.attrs({ as: ButtonBase })`
  ${contenderStyle}

  display: grid;
  grid-template-columns: calc(5 * var(--unit)) 1fr calc(5 * var(--unit));
  align-items: center;
  justify-content: space-between;

  background:
    linear-gradient(rgba(var(--rgb-color), 0.64), transparent) border-box,
    transparent;
  border: 1px solid rgba(var(--c-rgb-white), 0.07);

  > *:nth-child(2) {
    justify-content: space-evenly;
  }
`;

type Props = {
  so5LeaderboardGroupInterface: UpcomingFixtureLeague_so5LeaderboardGroupInterface;
  hideBorder?: boolean;
  scrollToContender?: (contender: string) => void;
};

export const UpcomingFixtureLeague = ({
  so5LeaderboardGroupInterface,
  hideBorder,
  scrollToContender,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const { navigateWithDeeplink } = useDeeplinkFromNativeApp();

  const { mySo5LeaderboardContenders, displayName, so5Fixture } =
    so5LeaderboardGroupInterface;
  const { sport } = so5Fixture;

  const [expandedGroupSlugPerFixture = {}, setExpandedGroupSlugPerFixture] =
    useSessionStorage(SESSION_STORAGE.expandedGroupSlugPerFixture);
  const [expanded, toggleExpanded] = useToggle(
    !!expandedGroupSlugPerFixture?.[so5Fixture.slug]?.includes(
      so5LeaderboardGroupInterface.slug
    )
  );

  const onToggleExpanded = () => {
    const oldFixtureExpanded =
      expandedGroupSlugPerFixture?.[so5Fixture.slug] || [];
    const newFixtureExpanded = expanded
      ? oldFixtureExpanded.filter(
          slug => slug !== so5LeaderboardGroupInterface.slug
        )
      : [...oldFixtureExpanded, so5LeaderboardGroupInterface.slug];

    setExpandedGroupSlugPerFixture({
      ...expandedGroupSlugPerFixture,
      [so5Fixture.slug]: newFixtureExpanded,
    });
    toggleExpanded();
  };

  const contendersSet = mySo5LeaderboardContenders.filter(
    contender => contender.so5Lineup?.confirmable
  );

  const showGreenChip =
    mySo5LeaderboardContenders.length === contendersSet.length;

  const [query, { data }] = useLazyQuery(LAZY_UPCOMING_FIXTURE_LEAGUE_QUERY, {
    variables: { slug: so5LeaderboardGroupInterface.slug },
  });

  // Query data if default expanded through session storage data
  useEffect(() => {
    if (!data && expanded) {
      query();
    }
  }, [data, expanded, query]);

  const addTeamPath = getAddTeamPath({
    so5LeaderboardGroupInterface: data?.so5.so5LeaderboardGroupInterface,
  });

  const footballSeasonalities = so5LeaderboardGroupInterface.seasonalities
    .length
    ? so5LeaderboardGroupInterface.seasonalities
    : [So5LeaderboardSeasonality.ALL_SEASONS];
  const seasonalities =
    sport === Sport.FOOTBALL
      ? footballSeasonalities
      : so5LeaderboardGroupInterface.seasonalities;

  const addTeamPathPerSeasonality = seasonalities.reduce(
    (acc, seasonality) => ({
      ...acc,
      [seasonality]: getAddTeamPath({
        so5LeaderboardGroupInterface: data?.so5.so5LeaderboardGroupInterface,
        seasonality,
      }),
    }),
    {} as Record<So5LeaderboardSeasonality, string>
  );

  return (
    <Root className={classNames({ hideBorder })}>
      <StyledButtonBase
        onClick={() => {
          if (!data && mySo5LeaderboardContenders.length > 0) {
            query();
          }
          onToggleExpanded();
        }}
      >
        <SBHorizontal>
          <Horizontal>
            <LeagueDot
              size={1}
              so5LeaderboardGroupInterface={so5LeaderboardGroupInterface}
            />
            <BodyM bold>{displayName}</BodyM>
            {!!mySo5LeaderboardContenders.length && (
              <Chip color={showGreenChip ? 'green' : 'transparent'}>
                <>
                  {contendersSet.length}
                  {' / '}
                  {mySo5LeaderboardContenders.length}
                </>
              </Chip>
            )}
            {!mySo5LeaderboardContenders.length &&
              isType(so5LeaderboardGroupInterface, 'So5League') &&
              so5LeaderboardGroupInterface.name === 'weekly' && (
                <Chip color="blue">
                  <FormattedMessage {...glossary.new} />
                </Chip>
              )}
          </Horizontal>
          <FontAwesomeIcon icon={expanded ? faAngleUp : faAngleDown} />
        </SBHorizontal>
      </StyledButtonBase>
      {expanded && (
        <Content gap={1.5}>
          {data ? (
            data.so5.so5LeaderboardGroupInterface.mySo5LeaderboardContenders.map(
              contender => (
                <Contender
                  key={contender.slug}
                  className={sport.toLowerCase()}
                  onClick={() => {
                    const { so5Lineup, so5Leaderboard } = contender;

                    if (so5Lineup) {
                      scrollToContender?.(contender.slug);
                    } else {
                      navigateWithDeeplink(
                        generateSportContextPath(
                          ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_COMPETITION_COMPETITION_TRCKGRP_TRCK,
                          {
                            params: {
                              fixture:
                                so5LeaderboardGroupInterface.so5Fixture.slug,
                              eventType:
                                so5LeaderboardGroupInterface.so5Fixture.type.toLowerCase(),
                              competition: so5LeaderboardGroupInterface.slug,
                              trck: so5Leaderboard.so5LeagueTrack.slug,
                              trckGrp: so5Leaderboard.so5LeagueTrack.groupSlug,
                            },
                          }
                        )
                      );
                    }
                  }}
                >
                  <Badge
                    size={5}
                    managerTeam={contender.managerTeam}
                    leaderboard={contender.so5Leaderboard}
                  />
                  {contender.so5Leaderboard.rules?.appearances?.map(
                    (appearanceRule, i) => {
                      const so5Appearance =
                        contender.so5Lineup?.so5Appearances.find(
                          app => app.index === i
                        );

                      const anyPlayerGameStats =
                        so5Appearance?.eligiblePlayerGameScores[0]
                          ?.anyPlayerGameStats;
                      const playerGameStats =
                        anyPlayerGameStats &&
                        isType(anyPlayerGameStats, 'PlayerGameStats')
                          ? anyPlayerGameStats
                          : null;

                      return (
                        <Vertical key={appearanceRule.name} center gap={0.5}>
                          <Avatar
                            src={
                              (so5Appearance?.avatarUrl &&
                                proxyUrl(so5Appearance.avatarUrl, {
                                  cropWidth: 80,
                                })) ||
                              emptyAppearance
                            }
                            alt=""
                            width="40"
                            height="40"
                          />
                          {playerGameStats?.footballPlayingStatusOdds && (
                            <LabelS bold>
                              <PlayerGameStatsPrediction
                                variant="text"
                                playingStatusOdds={
                                  playerGameStats.footballPlayingStatusOdds
                                }
                              />
                            </LabelS>
                          )}
                        </Vertical>
                      );
                    }
                  )}
                </Contender>
              )
            )
          ) : (
            <>
              {range(mySo5LeaderboardContenders.length).map(i => (
                <Contender key={i}>
                  {range(6).map(j => (
                    <AvatarSkeleton key={j} />
                  ))}
                </Contender>
              ))}
            </>
          )}
          {addTeamPath && (
            <>
              {!!mySo5LeaderboardContenders.length && (
                <SBHorizontal>
                  <LabelM bold>
                    <FormattedMessage {...fantasy.addTeam} />
                  </LabelM>
                  <LabelM color="var(--c-nd-600)">
                    <FormattedMessage {...fantasy.eligibleCards} />
                  </LabelM>
                </SBHorizontal>
              )}
              {seasonalities.map(seasonality => (
                <AddTeam
                  key={seasonality}
                  as={Link}
                  to={addTeamPathPerSeasonality[seasonality]}
                  style={
                    {
                      '--rgb-color': getRgbColorFromHex(
                        ensureHexColor(so5LeaderboardGroupInterface.bgColor)
                      ),
                    } as CSSProperties
                  }
                >
                  <CellWrapper>
                    <SeasonalityIcon seasonality={seasonality} size={3} />
                  </CellWrapper>
                  <SBHorizontal>
                    <EligibleCards
                      seasonality={seasonality}
                      so5LeaderboardGroupInterface={
                        so5LeaderboardGroupInterface
                      }
                    />
                  </SBHorizontal>
                  <CellWrapper>
                    <FontAwesomeIcon icon={faPlus} />
                  </CellWrapper>
                </AddTeam>
              ))}
            </>
          )}
        </Content>
      )}
    </Root>
  );
};

UpcomingFixtureLeague.fragments = {
  so5LeaderboardGroupInterface: gql`
    fragment UpcomingFixtureLeague_so5LeaderboardGroupInterface on So5LeaderboardGroupInterface {
      slug
      displayName
      seasonalities
      bgColor: color
      ... on So5League {
        slug
        name
      }
      mySo5LeaderboardContenders {
        slug
        so5Lineup {
          id
          confirmable
        }
      }
      so5Fixture {
        slug
        type
        sport
      }
      ...EligibleCards_so5LeaderboardGroupInterface
      ...LeagueDot_so5LeaderboardGroupInterface
    }
    ${EligibleCards.fragments.so5LeaderboardGroupInterface}
    ${LeagueDot.fragments.so5LeaderboardGroupInterface}
  ` as TypedDocumentNode<UpcomingFixtureLeague_so5LeaderboardGroupInterface>,
};
